import React, { useEffect, useRef, useState } from 'react';

const NAV_ITEMS = [
  { name: 'Home', key: 'home', href: '/', dropdown: null },
  {
    name: 'About Us',
    key: 'about',
    href: '/about',
    dropdown: null,
  },
  {
    name: 'Our Work',
    key: 'work',
    href: '/work',
    dropdown: null,
  },
  { name: 'Achievements', key: 'achievements', href: '/achievements', dropdown: null },
  {
    name: 'Members',
    key: 'members',
    href: '/members',
    dropdown: null,
  },
  {
    name: 'Governance',
    key: 'governance',
    href: '/governance',
    dropdown: null,
  },
  { name: 'News', key: 'news', href: '/news', dropdown: null },
];

const normalizePage = (value) => (value || '').toLowerCase().trim();

const Navbar = ({ currentPage, onNavigate }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isScrolledFar, setIsScrolledFar] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState('');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [expandedMobile, setExpandedMobile] = useState('');
  const [progressFlash, setProgressFlash] = useState(false);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      setIsScrolledFar(window.scrollY > 80);
      const total = document.body.scrollHeight - window.innerHeight;
      const progress = total > 0 ? (window.scrollY / total) * 100 : 0;
      const cappedProgress = Math.min(100, Math.max(0, progress));
      setScrollProgress(cappedProgress);
      setProgressFlash(cappedProgress > 95);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const currentKey = normalizePage(currentPage);
  const isItemActive = (item) => currentKey === item.key || currentKey === normalizePage(item.name);
  const isContactActive = currentKey === 'contact';

  const navigateTo = (targetKey) => {
    if (onNavigate) {
      onNavigate(targetKey);
    }
    setMobileOpen(false);
    setExpandedMobile('');
    setActiveDropdown('');
  };

  const handleMouseEnter = (name) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown('');
    }, 150);
  };

  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: 0% center; }
            100% { background-position: 200% center; }
          }
          @keyframes glow-breathe {
            0%, 100% { box-shadow: 0 0 8px rgba(0,229,204,0.2); }
            50% { box-shadow: 0 0 20px rgba(0,229,204,0.45); }
          }
          @keyframes progress-flash {
            0%, 100% { filter: brightness(1); }
            50% { filter: brightness(1.35); }
          }
        `}
      </style>

      <div className="fixed left-0 top-0 z-[60] h-[2px] w-full">
        <div
          className={`h-full transition-[width] duration-100 ${
            progressFlash ? 'animate-[progress-flash_0.5s_ease-in-out_infinite]' : ''
          }`}
          style={{
            width: `${scrollProgress}%`,
            background: 'linear-gradient(to right, #00E5CC, #00BFA5, #00E5CC)',
            backgroundSize: '200% auto',
            animation: `${progressFlash ? 'progress-flash 0.5s ease-in-out infinite, ' : ''}shimmer 2s linear infinite`,
            boxShadow: '0 0 8px rgba(0,229,204,0.6)',
          }}
        />
      </div>

      <header
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-500 ease-in-out ${
          isScrolled
            ? 'border-b border-white/15 bg-[#0B1629]/55 py-3 shadow-[0_8px_35px_rgba(0,0,0,0.45)] backdrop-blur-2xl backdrop-saturate-150'
            : 'border-b border-white/10 bg-[#0B1629]/35 py-5 backdrop-blur-xl backdrop-saturate-150'
        }`}
        style={{
          backdropFilter: isScrolled ? 'blur(24px) saturate(150%)' : 'blur(18px) saturate(140%)',
          WebkitBackdropFilter: isScrolled ? 'blur(24px) saturate(150%)' : 'blur(18px) saturate(140%)',
        }}
      >
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.08] via-white/[0.03] to-transparent" />
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6">
          <button
            type="button"
            onClick={() => navigateTo('home')}
            className="group flex cursor-pointer items-center gap-3"
            aria-label="Go to home"
          >
            <div
              className={`relative flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-[#00E5CC] via-[#00BFA5] to-cyan-700 p-[2px] transition-all duration-300 group-hover:scale-105 group-hover:shadow-[0_0_16px_rgba(0,229,204,0.5)] ${
                isScrolledFar ? 'animate-pulse ring-1 ring-[#00E5CC]/20' : ''
              }`}
            >
              <img
                src="/dingonetlogo-01.png"
                alt="DINGONET"
                className="h-full w-full rounded-[10px] object-contain bg-[#0B1629]"
              />
            </div>
            <span className="flex flex-col leading-none">
              <span
                className={`font-black tracking-wide text-white transition-all duration-500 group-hover:text-[#00E5CC] ${
                  isScrolled ? 'text-sm' : 'text-base'
                }`}
              >
                DINGONET
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#00E5CC]/70">
                Tanzania
              </span>
            </span>
          </button>

          <nav className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map((item) => {
              const active = isItemActive(item);
              const hasDropdown = !!item.dropdown;
              const dropdownOpen = activeDropdown === item.name;

              if (!hasDropdown) {
                return (
                  <button
                    key={item.name}
                    type="button"
                    onClick={() => navigateTo(item.key)}
                    className={`group relative whitespace-nowrap rounded-full border px-3 py-2 text-sm font-medium transition-all duration-200 ${
                      active
                        ? 'border-[#00E5CC]/30 bg-[#00E5CC]/15 text-white'
                        : 'border-transparent text-slate-400 hover:bg-white/5 hover:text-white'
                    }`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className="pointer-events-none absolute inset-0 rounded-full bg-[#00E5CC]/0 transition-colors duration-200 group-hover:bg-[#00E5CC]/6" />
                    <span className="relative">{item.name}</span>
                    {active ? (
                      <span className="absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[#00E5CC]" />
                    ) : null}
                    <span className="absolute bottom-0 left-3 right-3 h-[2px] scale-x-0 rounded-full bg-gradient-to-r from-[#00E5CC]/0 via-[#00E5CC] to-[#00E5CC]/0 transition-transform duration-300 group-hover:scale-x-100" />
                  </button>
                );
              }

              return (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(item.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    type="button"
                    onClick={() => navigateTo(item.key)}
                    className={`group relative whitespace-nowrap rounded-full border px-3 py-2 text-sm font-medium transition-all duration-200 ${
                      active
                        ? 'border-[#00E5CC]/30 bg-[#00E5CC]/15 text-white'
                        : 'border-transparent text-slate-400 hover:bg-white/5 hover:text-white'
                    }`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className="pointer-events-none absolute inset-0 rounded-full bg-[#00E5CC]/0 transition-colors duration-200 group-hover:bg-[#00E5CC]/6" />
                    <span className="relative flex items-center">
                      {item.name}
                      <svg
                        viewBox="0 0 24 24"
                        className={`ml-1 h-[14px] w-[14px] transition-transform duration-200 ${
                          dropdownOpen ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        aria-hidden="true"
                      >
                        <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    {active ? (
                      <span className="absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[#00E5CC]" />
                    ) : null}
                    <span className="absolute bottom-0 left-3 right-3 h-[2px] scale-x-0 rounded-full bg-gradient-to-r from-[#00E5CC]/0 via-[#00E5CC] to-[#00E5CC]/0 transition-transform duration-300 group-hover:scale-x-100" />
                    <span
                      className={`absolute -bottom-2 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-l border-t border-white/10 bg-[#0D1F38] transition-opacity duration-200 ${
                        dropdownOpen ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  </button>

                  <div
                    className={`absolute left-1/2 top-full z-50 mt-2 min-w-[240px] -translate-x-1/2 transition-all duration-200 ease-out ${
                      dropdownOpen
                        ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
                        : 'pointer-events-none -translate-y-2 scale-95 opacity-0'
                    }`}
                    onMouseEnter={() => {
                      if (closeTimeoutRef.current) {
                        clearTimeout(closeTimeoutRef.current);
                      }
                    }}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="rounded-2xl border border-white/15 bg-[#0D1F38]/65 p-2 shadow-[0_20px_60px_rgba(0,0,0,0.5),0_0_0_1px_rgba(0,229,204,0.08)] backdrop-blur-2xl backdrop-saturate-150">
                      <div className="mx-3 mb-2 h-[1px] bg-gradient-to-r from-transparent via-[#00E5CC]/30 to-transparent" />
                      {item.dropdown.map((entry, index) => (
                        <div key={entry.label}>
                          <button
                            type="button"
                            onClick={() => navigateTo(item.key)}
                            className="group/item flex w-full items-start gap-3 rounded-xl px-4 py-3 text-left transition-all duration-150 hover:bg-[#00E5CC]/8"
                          >
                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-sm transition-all duration-150 group-hover/item:border-[#00E5CC]/20 group-hover/item:bg-[#00E5CC]/15">
                              {entry.icon}
                            </span>
                            <span>
                              <span className="block text-sm font-medium text-slate-300 transition-colors duration-150 group-hover/item:text-white">
                                {entry.label}
                              </span>
                              <span className="mt-0.5 block text-xs text-slate-500 transition-colors duration-150 group-hover/item:text-slate-400">
                                {entry.sub}
                              </span>
                            </span>
                          </button>
                          {index < item.dropdown.length - 1 ? <div className="mx-4 h-px bg-white/[0.04]" /> : null}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>

          <div className="hidden md:block">
            <button
              type="button"
              onClick={() => navigateTo('contact')}
              className={`rounded-full bg-[#00E5CC] px-5 py-2.5 text-sm font-bold text-[#0B1629] transition-all duration-300 hover:scale-105 hover:bg-[#00BFA5] hover:shadow-[0_0_20px_rgba(0,229,204,0.35)] active:scale-95 animate-[glow-breathe_3s_ease-in-out_infinite] ${
                isContactActive ? 'ring-2 ring-[#00E5CC]/50 ring-offset-2 ring-offset-[#0B1629]' : ''
              }`}
            >
              Contact
            </button>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 transition-all duration-200 hover:border-[#00E5CC]/30 hover:bg-[#00E5CC]/8 md:hidden"
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
          >
            <span className="relative block h-5 w-5">
              <span
                className={`absolute left-0 top-1/2 block h-0.5 w-5 -translate-y-[6px] bg-white transition-all duration-300 ${
                  mobileOpen ? 'translate-y-0 rotate-45' : ''
                }`}
              />
              <span
                className={`absolute left-0 top-1/2 my-1 block h-0.5 w-5 -translate-y-1/2 bg-white transition-all duration-300 ${
                  mobileOpen ? 'scale-x-0 opacity-0' : 'opacity-100'
                }`}
              />
              <span
                className={`absolute left-0 top-1/2 block h-0.5 w-5 translate-y-[6px] bg-white transition-all duration-300 ${
                  mobileOpen ? 'translate-y-0 -rotate-45' : ''
                }`}
              />
            </span>
          </button>
        </div>
      </header>

      {mobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-[#0A1525]/70 opacity-100 backdrop-blur-2xl backdrop-saturate-150 transition-opacity duration-300 md:hidden"
          onClick={() => {
            setMobileOpen(false);
            setExpandedMobile('');
          }}
        >
          <div
            className="flex h-full flex-col overflow-y-auto px-6 pb-8 pt-20"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-8 h-[1px] bg-gradient-to-r from-transparent via-[#00E5CC]/30 to-transparent" />

            <div>
              {[...NAV_ITEMS, { name: 'Contact', key: 'contact', href: '/contact', dropdown: null }].map((item, index) => {
                const active = normalizePage(currentPage) === item.key || normalizePage(currentPage) === normalizePage(item.name);
                const expanded = expandedMobile === item.name;
                const hasDropdown = !!item.dropdown;

                if (!hasDropdown) {
                  return (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => navigateTo(item.key)}
                      className="flex w-full translate-x-0 items-center justify-between border-b border-white/10 py-4 text-left opacity-100 transition-all duration-300"
                      style={{ transitionDelay: `${mobileOpen ? index * 50 : 0}ms` }}
                    >
                      <span className="text-lg font-semibold text-white">{item.name}</span>
                      {active ? <span className="h-2 w-2 rounded-full bg-[#00E5CC]" /> : <span className="text-sm text-[#00E5CC]">→</span>}
                    </button>
                  );
                }

                return (
                  <div
                    key={item.name}
                    className="translate-x-0 border-b border-white/10 opacity-100 transition-all duration-300"
                    style={{ transitionDelay: `${mobileOpen ? index * 50 : 0}ms` }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedMobile(expanded ? '' : item.name);
                      }}
                      className="flex w-full items-center justify-between py-4 text-left"
                    >
                      <span className="text-lg font-semibold text-white">{item.name}</span>
                      <svg
                        viewBox="0 0 24 24"
                        className={`h-5 w-5 text-[#00E5CC] transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                        fill="none"
                        aria-hidden="true"
                      >
                        <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${expanded ? 'max-h-[500px]' : 'max-h-0'}`}>
                      <div className="flex flex-col gap-1 pb-3 pl-4 pt-2">
                        {item.dropdown.map((entry) => (
                          <button
                            key={entry.label}
                            type="button"
                            onClick={() => navigateTo(item.key)}
                            className="flex items-center gap-3 py-2.5 text-left"
                          >
                            <span className="text-lg">{entry.icon}</span>
                            <span className="text-sm text-slate-300 transition-colors hover:text-white">{entry.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-auto">
              <div className="mb-6 h-px bg-white/10" />
              <button
                type="button"
                onClick={() => navigateTo('contact')}
                className="w-full rounded-full bg-[#00E5CC] py-4 text-center text-base font-bold text-[#0B1629]"
              >
                Contact the Secretariat
              </button>
              <div className="mt-4 flex flex-col gap-2 text-xs text-slate-500">
                <span>📧 info@dingonet.org</span>
                <span>📞 Laurent Wambura, Executive Director · 0757724717</span>
                <span>📞 Suhaila Stanthon, Lead of Advocacy and Communication · 0744388179</span>
                <span>📍 Dar es Salaam, Tanzania</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Navbar;

